import httpClient from './httpClient';

const supplierService = {
  /**
   * Get list of suppliers with optional filtering
   * @param {Object} params - Query parameters
   * @param {string} params.supplier_name - Filter by supplier name
   * @param {number} params.page - Page number (default 1)
   * @param {number} params.page_size - Items per page (default 100)
   */
  async getSuppliers(params = {}) {
    try {
      const response = await httpClient.get('/api/suppliers', {
        params: {
          page: 1,
          page_size: 100,
          ...params
        }
      });
      // Normalize supplier data - handle different API response formats
      const data = response.data;
      let suppliers = [];
      
      // Handle both { suppliers: [...] } and direct array response
      if (Array.isArray(data)) {
        suppliers = data;
      } else if (data.suppliers && Array.isArray(data.suppliers)) {
        suppliers = data.suppliers;
      }
      
      // Normalize all suppliers
      suppliers = suppliers.map(supplier => this._normalizeSupplier(supplier));
      
      // Return consistent format: { suppliers: [...] }
      return {
        suppliers: suppliers,
        total: data.total || suppliers.length,
        ...data
      };
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error;
    }
  },

  /**
   * Normalize supplier data to consistent format
   * @param {Object} supplier - Raw supplier data from API
   * @returns {Object} Normalized supplier
   */
  _normalizeSupplier(supplier) {
    if (!supplier) return null;
    
    // Handle MongoDB ObjectId format: { $oid: 'xxx' }
    let id = supplier._id;
    if (id && typeof id === 'object' && id.$oid) {
      id = id.$oid;
    }
    
    // Use supplier_id if _id is not available
    if (!id && supplier.supplier_id) {
      id = supplier.supplier_id;
    }
    
    // Use supplier_name if available, otherwise use name
    const supplierName = supplier.supplier_name || supplier.name || '';
    
    return {
      ...supplier,
      _id: id,
      id: id,
      supplier_name: supplierName,
      name: supplierName
    };
  },

  /**
   * Search suppliers by name
   * @param {string} name - Search term
   */
  async searchSuppliers(name) {
    try {
      const response = await httpClient.get('/api/suppliers', {
        params: {
          supplier_name: name,
          page: 1,
          page_size: 50
        }
      });
      
      // Normalize supplier data - handle different API response formats
      const data = response.data;
      let suppliers = [];
      
      // Handle both { suppliers: [...] } and direct array response
      if (Array.isArray(data)) {
        suppliers = data;
      } else if (data.suppliers && Array.isArray(data.suppliers)) {
        suppliers = data.suppliers;
      }
      
      // Normalize all suppliers
      suppliers = suppliers.map(supplier => this._normalizeSupplier(supplier));
      
      // Return consistent format: { suppliers: [...] }
      return {
        suppliers: suppliers,
        total: data.total || suppliers.length,
        ...data
      };
    } catch (error) {
      console.error('Error searching suppliers:', error);
      throw error;
    }
  },

  /**
   * Get single supplier by ID
   * @param {string} supplierId - Supplier ID
   */
  async getSupplier(supplierId) {
    try {
      const response = await httpClient.get(`/api/suppliers/${supplierId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier:', error);
      throw error;
    }
  }
};

export default supplierService;
